import { RailsAPIBase, type paths } from "../index.client";
import type { Simplify } from "type-fest";

/**
 * Type definitions for upload operations
 */
export type GetUploadsRequest = {
  website_id?: number;
  key?: string;
  order?: "recent";
  limit?: number;
};

export type GetUploadsResponse = NonNullable<
  paths["/api/v1/uploads"]["get"]["responses"][200]["content"]["application/json"]
>;

export type CreateUploadResponse = NonNullable<
  paths["/api/v1/uploads"]["post"]["responses"][201]["content"]["application/json"]
>;

/** Request body for creating an upload - uses camelCase, converted to Rails format internally */
export interface CreateUploadRequest {
  file: File | Blob;
  isLogo?: boolean;
  websiteId?: number;
}

/** Upload type matching the GET /api/v1/uploads response */
export interface Upload {
  id: number;
  uuid: string;
  url: string;
  original_url?: string | null;
  favicon_url?: string | null;
  media_type: "image" | "video" | "document";
  is_logo: boolean;
  filename: string;
  created_at: string;
  updated_at: string;
}

/** Response from the presign endpoint */
export interface PresignResponse {
  url: string;
  key: string;
}

/**
 * Service for interacting with the Rails Uploads API
 * Can be used from both frontend and backend (langgraph)
 */
export class UploadsAPIService extends RailsAPIBase {
  constructor(options: Simplify<ConstructorParameters<typeof RailsAPIBase>[0]>) {
    // Multipart uploads are inherently slow. Both timeouts in the shared
    // fetch wrapper work against us here:
    //   - total timeout: 10MB phone photo over a congested uplink trivially
    //     blows past 10s.
    //   - "connect" timeout: with the standard fetch API, the response
    //     promise only resolves after the entire request body has been
    //     uploaded. So for a 20s upload, the 5s "connect" timer fires
    //     mid-body-send, the AbortController aborts, and the browser
    //     marks the fetch `(canceled)`. It's not really a connect
    //     timeout — it's a "time-to-response-headers" timeout, which
    //     for multipart POSTs is bounded by upload speed.
    // Both must be disabled for upload endpoints.
    super({
      timeoutMs: 0,
      connectTimeoutMs: 0,
      ...options,
    });
  }

  /**
   * Get uploads for the authenticated user
   * @param params - Optional query parameters (website_id, is_logo filters)
   * @returns List of uploads
   */
  async get(params?: GetUploadsRequest): Promise<GetUploadsResponse> {
    const client = await this.getClient();
    const response = await client.GET("/api/v1/uploads", {
      params: {
        query: params,
      },
    });

    if (response.response.status !== 200) {
      throw new Error(
        `Failed to get uploads: ${response.response.status} ${response.response.statusText}`
      );
    }

    if (response.error) {
      throw new Error(`Failed to get uploads: ${JSON.stringify(response.error)}`);
    }

    if (!response.data) {
      throw new Error(`Failed to get uploads: No data returned`);
    }

    return response.data satisfies GetUploadsResponse;
  }

  /**
   * Create a new upload using ActiveStorage direct upload.
   * Runs browser-side only: checksums the file, asks Rails for a signed blob,
   * PUTs the bytes straight to R2, then creates the Upload record with the
   * resulting signed_id. `@rails/activestorage` is dynamic-imported so this
   * module stays clean for the langgraph backend consumers that never call it.
   */
  async create(options: CreateUploadRequest): Promise<CreateUploadResponse> {
    const client = await this.getClient();

    const signedId = await this.directUploadBlob(options.file);

    const uploadBody: { signed_id: string; is_logo?: boolean; website_id?: number } = {
      signed_id: signedId,
    };
    if (options.isLogo !== undefined) uploadBody.is_logo = options.isLogo;
    if (options.websiteId !== undefined) uploadBody.website_id = options.websiteId;

    const response = await client.POST("/api/v1/uploads", {
      body: { upload: uploadBody },
    });

    if (response.response.status !== 201) {
      throw new Error(
        `Failed to create upload: ${response.response.status} ${response.response.statusText}`
      );
    }

    if (response.error) {
      throw new Error(`Failed to create upload: ${JSON.stringify(response.error)}`);
    }

    if (!response.data) {
      throw new Error(`Failed to create upload: No data returned`);
    }

    return response.data satisfies CreateUploadResponse;
  }

  /**
   * Drive `@rails/activestorage` DirectUpload for a single file and return
   * the resulting blob's signed_id. The DirectUpload class handles MD5
   * checksumming, the blob-creation POST to our API endpoint, and the PUT
   * of the bytes to R2. Dynamic import keeps this code out of the langgraph
   * bundle, which only consumes read methods.
   */
  private async directUploadBlob(file: File | Blob): Promise<string> {
    // @ts-expect-error — @rails/activestorage ships no type declarations
    const mod = await import("@rails/activestorage");
    const DirectUpload = mod.DirectUpload as new (
      file: File,
      url: string
    ) => {
      create(callback: (error: Error | null, blob: { signed_id: string }) => void): void;
    };

    const browserFile =
      typeof File !== "undefined" && file instanceof File
        ? file
        : new File([file], "upload", { type: (file as Blob).type });

    const directUploadsUrl = `${this.options.baseUrl ?? ""}/api/v1/direct_uploads`;

    return await new Promise<string>((resolve, reject) => {
      const upload = new DirectUpload(browserFile, directUploadsUrl);
      upload.create((error, blob) => {
        if (error) reject(error);
        else resolve(blob.signed_id);
      });
    });
  }

  /**
   * Delete an upload
   * @param uploadId - The upload ID to delete
   */
  async delete(uploadId: number): Promise<void> {
    const client = await this.getClient();
    const response = await client.DELETE("/api/v1/uploads/{id}", {
      params: {
        path: { id: uploadId },
      },
    });

    if (response.response.status !== 204) {
      throw new Error(
        `Failed to delete upload: ${response.response.status} ${response.response.statusText}`
      );
    }
  }

  /**
   * Update an upload (set is_logo, associate with website)
   */
  async update(
    uploadId: number,
    options: { isLogo?: boolean; websiteId?: number }
  ): Promise<Upload> {
    const client = await this.getClient();
    const body: Record<string, unknown> = {};
    if (options.isLogo !== undefined) body.is_logo = options.isLogo;
    if (options.websiteId !== undefined) body.website_id = options.websiteId;

    const response = await client.PATCH("/api/v1/uploads/{id}", {
      params: { path: { id: uploadId } },
      body: { upload: body } as any,
    });

    if (response.response.status !== 200) {
      throw new Error(
        `Failed to update upload: ${response.response.status} ${response.response.statusText}`
      );
    }

    if (response.error) {
      throw new Error(`Failed to update upload: ${JSON.stringify(response.error)}`);
    }

    if (!response.data) {
      throw new Error("Failed to update upload: No data returned");
    }

    return response.data as Upload;
  }

  /**
   * Find an upload by its ActiveStorage blob key. The key is the path
   * segment of the CDN URL: `{CDN_HOST}/{blob.key}`. Use this (paired
   * with `extractKeyFromUrl`) to resolve an Upload row from its URL.
   */
  async findByKey(key: string): Promise<Upload | null> {
    const uploads = (await this.get({ key })) as Upload[];
    return uploads[0] || null;
  }

  /**
   * Get a presigned URL for direct browser-to-R2 upload of the original file.
   */
  async presign(options: { extension: string; contentType: string }): Promise<PresignResponse> {
    const client = await this.getClient();
    const response = await client.GET("/api/v1/uploads/presign" as any, {
      params: {
        query: {
          extension: options.extension,
          content_type: options.contentType,
        },
      },
    });

    if (!response.data) {
      throw new Error("Failed to get presigned URL");
    }

    return response.data as PresignResponse;
  }

  /**
   * Confirm that the original file was uploaded to R2 via presigned URL.
   * Links the original file key to the upload record.
   */
  async confirmOriginal(uploadId: number, key: string): Promise<Upload> {
    const client = await this.getClient();
    const response = await client.POST("/api/v1/uploads/{id}/confirm_original" as any, {
      params: { path: { id: uploadId } },
      body: { key } as any,
    });

    if (!response.data) {
      throw new Error("Failed to confirm original upload");
    }

    return response.data as Upload;
  }

  /**
   * Extract the ActiveStorage blob key from an upload URL.
   * The URL format is `{CDN_HOST}/{blob.key}` (see Upload#url in Rails),
   * so the key is simply the first non-empty path segment.
   */
  static extractKeyFromUrl(url: string): string | null {
    if (!url) return null;
    try {
      const parsed = new URL(url);
      const key = parsed.pathname.replace(/^\/+/, "");
      return key.length > 0 ? key : null;
    } catch {
      return null;
    }
  }

  /**
   * Format uploads as image_url content blocks for LLM consumption.
   * Prefers original_url (full-res) when available, falls back to url (preview).
   */
  static formatForModel(images: Upload[]): Array<{ type: "image_url"; image_url: { url: string } }> {
    return images.map((image) => ({
      type: "image_url" as const,
      image_url: { url: image.original_url || image.url },
    }));
  }

  /**
   * Get the best available URL for an upload (original if available, otherwise preview).
   */
  static bestUrl(image: Upload): string {
    return image.original_url || image.url;
  }

  /**
   * Upload the original full-res file to R2 via presigned URL.
   *
   * Intended to be called fire-and-forget after the resized preview has
   * been uploaded via create(). The returned promise rejects on failure
   * so callers can report to Sentry — it does NOT swallow errors anymore.
   * Pass `onStage` to record progress breadcrumbs; this keeps shared/ free
   * of any Sentry/reporter dependency while letting rails_app wire one in.
   */
  async uploadOriginalInBackground(
    uploadId: number,
    originalFile: File,
    onStage?: (stage: "presign_start" | "presign_ok" | "r2_put_ok" | "confirm_ok") => void
  ): Promise<void> {
    const ext = originalFile.name.split(".").pop()?.toLowerCase() || "jpg";

    onStage?.("presign_start");
    const { url, key } = await this.presign({
      extension: ext,
      contentType: originalFile.type || "image/jpeg",
    });
    onStage?.("presign_ok");

    const putResponse = await fetch(url, {
      method: "PUT",
      headers: { "Content-Type": originalFile.type || "image/jpeg" },
      body: originalFile,
    });
    if (!putResponse.ok) {
      throw new Error(
        `R2 PUT failed: ${putResponse.status} ${putResponse.statusText}`
      );
    }
    onStage?.("r2_put_ok");

    await this.confirmOriginal(uploadId, key);
    onStage?.("confirm_ok");
  }
}

// Re-export with old name for backwards compatibility during migration
export { UploadsAPIService as UploadService };
